import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, Grid, IconButton, InputAdornment, Paper,
  Tab, Tabs, TextField, Tooltip, Typography, alpha, useTheme
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { ptBR } from "date-fns/locale";
import dayjs from 'dayjs';
import { Field, Form, Formik } from "formik";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from 'tss-react/mui';
import * as Yup from "yup";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import QueueSelect from "../QueueSelect";

// Ícones
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FacebookIcon from '@mui/icons-material/Facebook';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ForumIcon from '@mui/icons-material/Forum';
import GroupsIcon from '@mui/icons-material/Groups';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import InstagramIcon from '@mui/icons-material/Instagram'; // reativar junto do canal instagram
import KeyIcon from '@mui/icons-material/Key';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MessageIcon from '@mui/icons-material/Message';
import NumbersIcon from '@mui/icons-material/Numbers';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SaveIcon from '@mui/icons-material/Save';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SettingsIcon from '@mui/icons-material/Settings';
// import TelegramIcon from '@mui/icons-material/Telegram'; // reativar junto do canal telegram
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nome muito curto")
    .max(50, "Nome muito longo")
    .required("Nome é obrigatório"),
  greetingMessage: Yup.string()
    .when('ignoreChatbot', {
      is: false,
      then: () => Yup.string().required("Mensagem de saudação é obrigatória"),
      otherwise: () => Yup.string().notRequired()
    }),
  greetingMessage2: Yup.string().nullable(),
  greetingMessage3: Yup.string().nullable(),
  message: Yup.string()
    .when(['hasQueues', 'ignoreChatbot'], {
      is: (hasQueues, ignoreChatbot) => hasQueues && !ignoreChatbot,
      then: () => Yup.string().required("Mensagem de setores é obrigatória"),
      otherwise: () => Yup.string().notRequired()
    }),
  message2: Yup.string().nullable(),
  message3: Yup.string().nullable(),
  farewellMessage: Yup.string()
    .when('ignoreChatbot', {
      is: false,
      then: () => Yup.string().required("Mensagem de despedida é obrigatória"),
      otherwise: () => Yup.string().notRequired()
    }),
  farewellMessage2: Yup.string().nullable(),
  farewellMessage3: Yup.string().nullable(),
  closedMessage: Yup.string()
    .when('ignoreChatbot', {
      is: false,
      then: () => Yup.string().required("Mensagem de expediente fechado é obrigatória"),
      otherwise: () => Yup.string().notRequired()
    }),
  closedMessage2: Yup.string().nullable(),
  closedMessage3: Yup.string().nullable(),
  autoCloseMessage: Yup.string()
    .when('autoClose', {
      is: true,
      then: () => Yup.string().required("Mensagem de auto fechamento é obrigatória"),
    }),
  token: Yup.string()
    .when('channel', {
      is: 'telegram',
      then: () => Yup.string().required("Token da API é obrigatório para o Telegram"),
    }),
  uniserviceContract: Yup.string()
    .nullable()
    .when('channel', {
      is: 'uniservice',
      then: () => Yup.string()
        .required("Contrato é obrigatório para conexão via API Oficial")
        .matches(/^[0-9A-Za-z._-]+$/, "Contrato pode conter apenas letras, números, ponto, hífen e underscore"),
    }),
  ignoreChatbot: Yup.boolean(),
});

// Os 3 primeiros são as variantes de WhatsApp, em ordem crescente de
// estabilidade (Básico → Avançado → Oficial) — a descrição de cada card deixa
// explícita a diferença, que era a fonte da confusão. `group` agrupa visual-
// mente; `badge` orienta a escolha; `disabled` desabilita os "Em breve".
const channels = [
  {
    value: "whatsapp",
    group: "WhatsApp",
    disabled: false,
    color: "#cfcc00",
    Icon: WhatsAppIcon,
    labelKey: "whatsappModal.channel.whatsapp",
    description: "Conexão via QR Code (gratuita). Instável e com risco de banimento pela Meta."
  },
  {
    value: "whatsgo",
    group: "WhatsApp",
    disabled: false,
    color: "#128C7E",
    Icon: WhatsAppIcon,
    badge: "Recomendado",
    labelKey: "whatsappModal.channel.whatsgo",
    description: "Conexão via QR Code estável (WhatsGo). Recomendada para o dia a dia."
  },
  {
    value: "uniservice",
    group: "WhatsApp",
    disabled: false,
    color: "#25D366",
    Icon: WhatsAppIcon,
    labelKey: "whatsappModal.channel.whatsappOfficial",
    description: "API Oficial da Meta via UniService. Máxima estabilidade e segurança."
  },
  // Demais canais ocultos por ora — descomente para reativar na seleção.
  // (Telegram continua funcional no backend; só não é ofertado na UI.)
  // {
  //   value: "telegram",
  //   group: "Outros",
  //   disabled: false,
  //   color: "#3390ec",
  //   Icon: TelegramIcon,
  //   labelKey: "whatsappModal.channel.telegram",
  //   description: "Bot do Telegram via token."
  // },
  // {
  //   value: "webchat",
  //   group: "Outros",
  //   disabled: true,
  //   color: "#0573e7",
  //   Icon: ForumIcon,
  //   labelKey: "whatsappModal.channel.webchat",
  //   description: "Chat ao vivo no seu site."
  // },
  // {
  //   value: "instagram",
  //   group: "Outros",
  //   disabled: true,
  //   color: "#E1306C",
  //   Icon: InstagramIcon,
  //   labelKey: "whatsappModal.channel.instagram",
  //   description: "Mensagens diretas do Instagram."
  // },
  // {
  //   value: "messenger",
  //   group: "Outros",
  //   disabled: true,
  //   color: "#0084FF",
  //   Icon: FacebookIcon,
  //   labelKey: "whatsappModal.channel.messenger",
  //   description: "Atenda via Facebook Messenger."
  // },
];

// Grupos derivados dos canais ATIVOS (na ordem em que aparecem). Assim, ao
// (des)comentar canais acima, os cabeçalhos de grupo se ajustam sozinhos e
// nenhum grupo vazio é renderizado.
const channelGroups = [...new Set(channels.map((c) => c.group))];

const keywordCategories = [
  {
    id: 'cliente',
    title: 'Cliente',
    icon: <AccessibilityNewIcon fontSize="small" />,
    color: '#4C6EF5'
  },
  {
    id: 'atendimento',
    title: 'Atendimento',
    icon: <QuestionAnswerIcon fontSize="small" />,
    color: '#FA5252'
  },
  {
    id: 'empresa',
    title: 'Empresa',
    icon: <BusinessIcon fontSize="small" />,
    color: '#7950F2'
  },
  {
    id: 'dataHora',
    title: 'Data e Hora',
    icon: <AccessTimeIcon fontSize="small" />,
    color: '#12B886'
  }
];

const availableKeywords = [
  {
    key: '{{cliente}}',
    label: 'Nome do Cliente',
    description: 'Insere o nome do cliente automaticamente',
    example: 'Olá, {{cliente}}!',
    icon: <AccessibilityNewIcon />,
    category: 'cliente'
  },
  {
    key: '{{cliente.nome}}',
    label: 'Primeiro nome do cliente',
    description: 'Exibe apenas o primeiro nome do cliente',
    example: 'Bom dia, {{cliente.nome}}!',
    icon: <AccessibilityNewIcon />,
    category: 'cliente'
  },
  {
    key: '{{cliente.email}}',
    label: 'Email do cliente',
    description: 'Insere o endereço de email do cliente',
    example: 'Confirmamos seu acesso em {{cliente.email}}',
    icon: <AlternateEmailIcon />,
    category: 'cliente'
  },
  {
    key: '{{protocolo}}',
    label: 'Protocolo',
    description: 'Insere o número do protocolo do atendimento',
    example: 'Seu protocolo: {{protocolo}}',
    icon: <NumbersIcon />,
    category: 'atendimento'
  },
  {
    key: '{{atendente}}',
    label: 'Nome do Atendente',
    description: 'Insere o nome do atendente atual',
    example: 'Meu nome é {{atendente}}, como posso ajudar?',
    icon: <AlternateEmailIcon />,
    category: 'atendimento'
  },
  {
    key: '{{empresa}}',
    label: 'Nome da Empresa',
    description: 'Insere o nome da sua empresa conforme cadastrado',
    example: 'A {{empresa}} agradece seu contato',
    icon: <BusinessIcon />,
    category: 'empresa'
  },
  {
    key: '{{data}}',
    label: 'Data Atual',
    description: 'Insere a data atual no formato DD/MM/AAAA',
    example: 'Hoje é {{data}}',
    icon: <EventAvailableIcon />,
    category: 'dataHora'
  },
  {
    key: '{{hora}}',
    label: 'Hora Atual',
    description: 'Insere a hora atual no formato HH:MM',
    example: 'Agora são {{hora}}',
    icon: <ScheduleIcon />,
    category: 'dataHora'
  },
  {
    key: '{{saudacao}}',
    label: 'Saudação Dinâmica',
    description: 'Insere uma saudação de acordo com o horário atual',
    example: '{{saudacao}}, em que posso ajudar?',
    icon: <AccessTimeIcon />,
    category: 'dataHora'
  },
];

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    dialogRoot: {
      '& .MuiDialog-paper': {
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
        maxWidth: 1000,
        width: '100%',
        margin: 24,
      },
      '& .MuiBackdrop-root': {
        backgroundColor: alpha(theme.palette.background.default, 0.8)
      }
    },
    dialogTitle: {
      background: isDark
        ? alpha(theme.palette.primary.dark, 0.9)
        : theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      padding: theme.spacing(2, 3),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& h6': {
        fontSize: '1.2rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      }
    },
    dialogContent: {
      padding: theme.spacing(3),
      backgroundColor: isDark
        ? theme.palette.background.default
        : alpha(theme.palette.background.paper, 0.8),
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 220px)',
    },
    dialogActions: {
      padding: theme.spacing(2, 3),
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.3)
        : alpha(theme.palette.background.default, 0.5),
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1.5),
    },
    formSection: {
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2.5),
      borderRadius: 12,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.2)
        : theme.palette.background.paper,
      boxShadow: isDark
        ? `0 4px 12px ${alpha('#000', 0.1)}`
        : `0 4px 12px ${alpha('#000', 0.05)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        boxShadow: isDark
          ? `0 6px 16px ${alpha('#000', 0.14)}`
          : `0 6px 16px ${alpha('#000', 0.08)}`,
      }
    },
    sectionTitle: {
      marginBottom: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.2),
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '1.1rem',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      paddingBottom: theme.spacing(1.2),
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: -1,
        width: 40,
        height: 2,
        backgroundColor: theme.palette.primary.main,
        borderRadius: 2
      }
    },
    icon: {
      fontSize: 20,
      color: theme.palette.primary.main,
    },
    channelIcon: {
      marginRight: theme.spacing(1),
      fontSize: '1.2rem',
    },
    channelCard: {
      cursor: 'pointer',
      padding: theme.spacing(2),
      borderRadius: 8,
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      backgroundColor: isDark
        ? alpha(theme.palette.background.default, 0.4)
        : alpha(theme.palette.background.paper, 0.7),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      position: 'relative',
      transition: 'all 0.2s ease',
      height: '100%',
      '&:hover': {
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.3)
          : alpha(theme.palette.background.paper, 0.9),
        borderColor: alpha(theme.palette.primary.main, 0.3),
        transform: 'translateY(-2px)',
      },
      '&.selected': {
        borderColor: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
      }
    },
    channelIconWrapper: {
      width: 48,
      height: 48,
      borderRadius: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(1.5),
      '& svg': {
        fontSize: 24,
      }
    },
    channelGroupLabel: {
      fontSize: '0.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.75),
      marginTop: theme.spacing(0.5),
    },
    channelBadge: {
      position: 'absolute',
      top: 8,
      left: 8,
      fontSize: '0.6rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      padding: '2px 8px',
      borderRadius: 12,
      color: theme.palette.success.main,
      backgroundColor: alpha(theme.palette.success.main, 0.14),
    },
    channelTitle: {
      fontWeight: 600,
      fontSize: '0.85rem',
      lineHeight: 1.3,
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.primary,
    },
    channelDescription: {
      fontSize: '0.72rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.35,
    },
    channelCheckmark: {
      position: 'absolute',
      top: 8,
      right: 8,
      color: theme.palette.primary.main,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.7)
        : alpha(theme.palette.background.paper, 0.9),
      borderRadius: '50%',
      padding: 2,
      fontSize: '1rem',
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.2)
          : theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.3)
            : theme.palette.background.paper,
        },
        '&.Mui-focused': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        }
      },
      '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1.5, 1.5),
        fontSize: '0.9rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.9rem',
        fontWeight: 500,
      },
      '& .MuiFormHelperText-root': {
        marginLeft: 4,
        marginTop: 4,
        fontSize: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        '&.Mui-error': {
          backgroundColor: alpha(theme.palette.error.main, 0.08),
          color: theme.palette.error.main,
          padding: theme.spacing(0.4, 0.8),
          borderRadius: 4,
        }
      }
    },
    warningAlert: {
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
      borderRadius: 8,
      padding: theme.spacing(1.5, 2),
      marginTop: theme.spacing(2),
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      '& svg': {
        color: theme.palette.warning.main,
        marginTop: '2px'
      },
      '& p': {
        color: theme.palette.text.primary,
        fontSize: '0.85rem',
        margin: 0
      }
    },
    successAlert: {
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
      borderRadius: 8,
      padding: theme.spacing(1.5, 2),
      marginTop: theme.spacing(2),
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      '& svg': {
        color: theme.palette.success.main,
        marginTop: '2px'
      },
      '& p': {
        color: theme.palette.text.primary,
        fontSize: '0.85rem',
        margin: 0
      }
    },
    fieldLabel: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '0.9rem',
      '& svg': {
        marginRight: theme.spacing(0.8),
        color: theme.palette.primary.main,
        fontSize: '1.1rem',
        opacity: 0.9,
      }
    },
    textFieldWithTags: {
      position: 'relative',
      marginBottom: theme.spacing(2),
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -10,
      marginLeft: -10,
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(5),
      flexDirection: "column",
      gap: theme.spacing(2.5),
      minHeight: 300,
      '& .MuiCircularProgress-root': {
        color: theme.palette.primary.main,
      },
      '& .MuiTypography-root': {
        color: theme.palette.text.secondary,
        fontWeight: 500,
        fontSize: '0.95rem',
      }
    },
    helpIcon: {
      fontSize: 16,
      marginLeft: theme.spacing(0.5),
      color: alpha(theme.palette.text.secondary, 0.6),
      cursor: 'pointer',
      transition: 'color 0.2s',
      '&:hover': {
        color: theme.palette.primary.main
      }
    },
    saveButton: {
      borderRadius: 8,
      padding: theme.spacing(1, 3),
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.9rem',
      boxShadow: theme.shadows[2],
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: theme.shadows[4],
      }
    },
    cancelButton: {
      borderRadius: 8,
      padding: theme.spacing(1, 2.5),
      fontWeight: 500,
      textTransform: 'none',
      fontSize: '0.9rem',
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: alpha(theme.palette.divider, 0.1),
      }
    },
    switchRoot: {
      width: 42,
      height: 24,
      padding: 0,
      margin: 0,
      '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        '&.Mui-checked': {
          transform: 'translateX(18px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.main,
            opacity: 0.85,
            border: 0,
          },
        },
      },
      '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      },
      '& .MuiSwitch-track': {
        borderRadius: 26,
        opacity: 0.38,
        backgroundColor: theme.palette.text.secondary,
        boxSizing: 'border-box',
        transition: 'all 0.2s',
      },
    },
    infoTooltip: {
      maxWidth: 250,
      fontSize: '0.8rem',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      borderRadius: 8,
      padding: theme.spacing(1.2, 1.5),
      lineHeight: 1.5,
    },
    optionsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.6),
      padding: theme.spacing(1.8, 2),
      borderRadius: 8,
      margin: theme.spacing(2, 0, 3),
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },
    optionBlock: {
      flex: '1',
      minWidth: 200,
      maxWidth: 300,
    },
    compactFormControl: {
      margin: 0,
      '& .MuiFormControlLabel-label': {
        fontSize: '0.9rem',
        fontWeight: 500,
      },
    },
    keywordsPanel: {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 10,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.6),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
      zIndex: 10,
    },
    keywordsHeader: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.8),
      paddingBottom: theme.spacing(1.2),
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
      '& svg': {
        color: theme.palette.primary.main,
      }
    },
    keywordsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.75),
    },
    keywordChip: {
      height: 32,
      borderRadius: 16,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.25)
        : alpha(theme.palette.background.paper, 0.8),
      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      color: theme.palette.text.primary,
      fontSize: '0.8rem',
      fontWeight: 500,
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: alpha(theme.palette.primary.main, 0.3),
        boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.12)}`,
      },
      '& .MuiChip-icon': {
        color: theme.palette.primary.main,
        marginLeft: 4,
      },
      '& .MuiChip-label': {
        padding: '0 10px',
      }
    },
    keywordCategoryTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.8),
      fontSize: '0.85rem',
      fontWeight: 600,
      padding: theme.spacing(0.8, 1.5),
      borderRadius: 8,
      marginBottom: theme.spacing(1.2),
      marginTop: theme.spacing(1.8),
      '&:first-of-type': {
        marginTop: 0,
      },
      '& svg': {
        fontSize: '0.95rem',
      }
    },
    loadingCircle: {
      color: theme.palette.primary.main,
    },
    errorText: {
      color: theme.palette.error.main,
      fontSize: '0.75rem',
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    fieldTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.6),
      marginTop: theme.spacing(0.8),
      marginBottom: theme.spacing(0.8),
    },
    activeKeywordTag: {
      height: 26,
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      borderRadius: 13,
      transition: 'all 0.15s ease',
      '& .MuiChip-label': {
        padding: theme.spacing(0, 0.75),
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15),
      }
    },
    closeButton: {
      color: theme.palette.primary.contrastText,
      backgroundColor: alpha(theme.palette.common.white, 0.1),
      borderRadius: 8,
      padding: 6,
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.2),
      }
    },
    timePicker: {
      '& .MuiInputBase-root': {
        borderRadius: 8,
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.2)
          : theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.3)
            : theme.palette.background.paper,
        },
        '&.Mui-focused': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        }
      },
      '& .MuiInputBase-input': {
        padding: theme.spacing(1.5, 1.5),
      }
    },
    saveProgress: {
      color: '#fff',
      marginRight: theme.spacing(1),
    },
    titleWithIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    hintText: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.8),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.6),
      padding: theme.spacing(0.8, 1.2),
      borderRadius: 8,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.7),
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      '& svg': {
        fontSize: '1rem',
        color: theme.palette.primary.main,
        opacity: 0.8,
      }
    },
    tokenField: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      '& .MuiTextField-root': {
        flex: 1,
      }
    },
    tokenVisibilityButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.2)
        : alpha(theme.palette.background.default, 0.7),
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.3)
          : alpha(theme.palette.background.default, 0.9),
        color: theme.palette.primary.main,
      }
    },
    divider: {
      margin: theme.spacing(2.5, 0),
      opacity: 0.6,
    },
    timeInfo: {
      marginTop: theme.spacing(0.8),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.8),
      fontSize: '0.85rem',
      fontWeight: 500,
      padding: theme.spacing(0.8, 1.2),
      borderRadius: 8,
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
      width: 'fit-content',
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      '& svg': {
        fontSize: '1rem',
      }
    },
    messageTabsContainer: {
      marginBottom: theme.spacing(2.5),
      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      borderRadius: 8,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.6),
      padding: theme.spacing(0.5),
    },
    messageTabs: {
      minHeight: 'unset',
      '& .MuiTab-root': {
        textTransform: 'none',
        minHeight: 40,
        padding: theme.spacing(0.8, 1.5),
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        borderRadius: 6,
        '&.Mui-selected': {
          color: theme.palette.primary.main,
          fontWeight: 600,
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.3)
            : alpha(theme.palette.background.paper, 0.6),
          boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.05)}`,
        }
      },
      '& .MuiTabs-indicator': {
        display: 'none',
      }
    },
    messageTabIcon: {
      marginRight: theme.spacing(0.8),
      fontSize: '1.1rem',
    },
    floatingSuccessAlert: {
      position: 'fixed',
      bottom: 24,
      right: 24,
      backgroundColor: theme.palette.success.main,
      color: theme.palette.common.white,
      padding: theme.spacing(1.2, 2),
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 2000,
      '& .MuiSvgIcon-root': {
        fontSize: '1.2rem',
      },
      '& .MuiTypography-root': {
        fontWeight: 500,
        fontSize: '0.9rem',
      }
    }
  };
});

// TextFieldWithPassword Component
const TextFieldWithPassword = ({
  name,
  label,
  placeholder,
  icon,
  touched,
  errors,
  classes
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Box className={classes.tokenField}>
      <Field
        as={TextField}
        placeholder={placeholder}
        type={showPassword ? "text" : "password"}
        fullWidth
        name={name}
        variant="outlined"
        size="small"
        error={touched[name] && Boolean(errors[name])}
        helperText={touched[name] && errors[name]}
        className={classes.textField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {icon}
            </InputAdornment>
          ),
        }}
      />
      <IconButton
        className={classes.tokenVisibilityButton}
        onClick={() => setShowPassword(!showPassword)}
        aria-label={showPassword ? "Ocultar" : "Mostrar"}
        size="small"
      >
        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
};

// TextFieldWithKeywords Component
const TextFieldWithKeywords = ({
  fieldName,
  label,
  placeholder,
  fieldRef,
  values,
  touched,
  errors,
  setFieldValue,
  handleFieldFocus,
  handleFieldBlur,
  handleSelectionChange,
  handleTextFieldKeyUp,
  handleTextFieldMouseUp,
  handleInsertKeyword,
  focusedField,
  classes,
  multiline = true,
  minRows = 3,
  maxRows = 5,
  icon = <MessageIcon fontSize="small" />
}) => {
  const [showKeywords, setShowKeywords] = useState(false);
  const theme = useTheme();

  const keywordsInText = useMemo(() => {
    if (!values[fieldName]) return [];
    const regex = /\{\{([^{}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(values[fieldName])) !== null) {
      if (!matches.some(m => m.value === match[0])) {
        matches.push({
          value: match[0],
          keyword: match[1]
        });
      }
    }
    return matches;
  }, [values[fieldName]]);

  const handleFocus = () => {
    handleFieldFocus(fieldName);
    setShowKeywords(true);
  };

  const handleBlur = (e) => {
    if (e.relatedTarget && e.relatedTarget.closest('.MuiChip-root')) {
      return;
    }
    handleFieldBlur();
    setTimeout(() => {
      setShowKeywords(false);
    }, 200);
  };

  return (
    <Box sx={{ position: 'relative', mb: 2.5 }}>
      <Typography className={classes.fieldLabel}>
        {icon} {label}
      </Typography>

      <Field
        as={TextField}
        placeholder={placeholder}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        fullWidth
        name={fieldName}
        error={touched[fieldName] && Boolean(errors[fieldName])}
        helperText={touched[fieldName] && errors[fieldName]}
        variant="outlined"
        size="small"
        className={`${classes.textField}`}
        inputRef={fieldRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSelect={handleSelectionChange}
        onKeyUp={handleTextFieldKeyUp}
        onMouseUp={handleTextFieldMouseUp}
        onClick={handleSelectionChange}
        InputProps={{
          style: { fontSize: '0.9rem' },
        }}
      />

      {keywordsInText.length > 0 && (
        <Box className={classes.fieldTags}>
          {keywordsInText.map((item, index) => (
            <Chip
              key={`${item.value}-${index}`}
              size="small"
              label={item.value}
              className={classes.activeKeywordTag}
              icon={<LocalOfferIcon sx={{ fontSize: '0.75rem' }} />}
            />
          ))}
        </Box>
      )}

      {showKeywords && (
        <Box className={classes.keywordsPanel}>
          <Box className={classes.keywordsHeader}>
            <LocalOfferIcon sx={{ fontSize: '1rem' }} />
            <Typography>
              Palavras-chave disponíveis
            </Typography>
          </Box>

          {keywordCategories.map(category => {
            const categoryKeywords = availableKeywords.filter(
              keyword => keyword.category === category.id
            );

            if (categoryKeywords.length === 0) return null;

            return (
              <Box key={category.id} sx={{ mb: 1 }}>
                <Box
                  className={classes.keywordCategoryTitle}
                  sx={{
                    color: category.color,
                    borderLeft: `3px solid ${category.color}`,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#edecec'
                  }}
                >
                  {category.icon}
                  <Typography variant="subtitle2">
                    {category.title}
                  </Typography>
                </Box>

                <Box className={classes.keywordsContainer}>
                  {categoryKeywords.map(keyword => (
                    <Chip
                      key={keyword.key}
                      label={keyword.label}
                      icon={keyword.icon}
                      className={classes.keywordChip}
                      onClick={() => handleInsertKeyword(keyword.key, setFieldValue, values)}
                      title={keyword.description}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const CreateOrEditWhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const { user: loggedInUser } = useContext(AuthContext);
  const theme = useTheme();
  const { classes } = useStyles();

  const initialState = {
    companyId: loggedInUser?.companyId || "",
    name: "",
    greetingMessage: "",
    greetingMessage2: "",
    greetingMessage3: "",
    message: "",
    message2: "",
    message3: "",
    farewellMessage: "",
    farewellMessage2: "",
    farewellMessage3: "",
    closedMessage: "",
    closedMessage2: "",
    closedMessage3: "",
    title: "",
    isDefault: false,
    autoCloseMessage: "",
    token: "",
    ignoreChatbot: false, // Novo campo adicionado
    uniserviceContract: "",
  };

  const [activeMessageTab, setActiveMessageTab] = useState(0);
  const [greetingTab, setGreetingTab] = useState(0);
  const [messageTab, setMessageTab] = useState(0);
  const [farewellTab, setFarewellTab] = useState(0);
  const [closedMessageTab, setClosedMessageTab] = useState(0);
  const [autoClose, setAutoClose] = useState(false);
  const [useTyping, setUseTyping] = useState(false);
  const [insistAnswering, setInsistAnswering] = useState(true);
  const [answerInGroups, setAnswerInGroups] = useState(true);
  const [answerInPrivate, setAnswerInPrivate] = useState(true);
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [channel, setChannel] = useState('whatsapp');
  const [loading, setLoading] = useState(true);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [timeToAutoClose, setTimeToAutoClose] = useState(dayjs('2022-04-07 00:30:00'));
  const [autoCloseTime, setTimeToAutoCloseMilliseconds] = useState(1800000);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTokenPassword, setShowTokenPassword] = useState(false);

  const [activeField, setActiveField] = useState(null);
  const [fieldSelectionStart, setFieldSelectionStart] = useState(0);
  const [fieldSelectionEnd, setFieldSelectionEnd] = useState(0);
  const [insertedKeyword, setInsertedKeyword] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const greetingMessageRef = useRef(null);
  const greetingMessage2Ref = useRef(null);
  const greetingMessage3Ref = useRef(null);
  const messageRef = useRef(null);
  const message2Ref = useRef(null);
  const message3Ref = useRef(null);
  const farewellMessageRef = useRef(null);
  const farewellMessage2Ref = useRef(null);
  const farewellMessage3Ref = useRef(null);
  const closedMessageRef = useRef(null);
  const closedMessage2Ref = useRef(null);
  const closedMessage3Ref = useRef(null);
  const autoCloseMessageRef = useRef(null);

  const successTimeoutRef = useRef(null);

  function msToTime(duration) {
    const minutes = Math.floor((duration / (1000 * 60)) % 60);
    const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
    const formattedHours = (hours < 10) ? "0" + hours : hours;
    const formattedMinutes = (minutes < 10) ? "0" + minutes : minutes;
    return `${formattedHours}:${formattedMinutes}`;
  }

  useEffect(() => {
    const fetchSession = async () => {
      if (whatsAppId) {
        try {
          const { data } = await api.get(`whatsapp/${whatsAppId}`);
          setWhatsApp(data);
          setChannel(data.channel || 'whatsapp');
          setAutoClose(data.autoClose || false);
          setUseTyping(data.useTyping || false);
          setInsistAnswering(data.insistAnswering !== false);
          setAnswerInGroups(data.answerInGroups !== false);
          setAnswerInPrivate(data.answerInPrivate !== false);

          if (data?.autoCloseTime) {
            const timeInMs = Number(data.autoCloseTime);
            setTimeToAutoCloseMilliseconds(timeInMs);
            const date = new Date();
            date.setHours(0, 0, 0, 0);
            date.setMilliseconds(timeInMs);
            setTimeToAutoClose(dayjs(date));
          }

          if (data?.queuesSequence) {
            try {
              const reorder = JSON.parse(data.queuesSequence);
              setSelectedQueueIds(Array.isArray(reorder) ? reorder : []);
            } catch (err) {
              console.error("Error parsing queuesSequence:", err);
              const whatsQueueIds = data.queues?.map(queue => queue.id) || [];
              setSelectedQueueIds(whatsQueueIds);
            }
          } else {
            const whatsQueueIds = data.queues?.map(queue => queue.id) || [];
            setSelectedQueueIds(whatsQueueIds);
          }

        } catch (err) {
          ToastError(err);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    if (open) {
      fetchSession();
    }
  }, [whatsAppId, open]);

  const handleSaveWhatsApp = async (values, { resetForm, setErrors, setSubmitting }) => {
    console.info('entrei')
    try {
      setIsSubmitting(true);
      const dataToSend = {
        "companyId": values.companyId,
        "name": values.name,
        "greetingMessage": values.greetingMessage,
        "message": values.message,
        "farewellMessage": values.farewellMessage,
        "closedMessage": values.closedMessage,
        "isDefault": values.isDefault,
        "token": values.token,
        "queueIds": selectedQueueIds,
        "channel": channel,
        "autoCloseMessage": values.autoCloseMessage,
        "autoClose": autoClose,
        "insistAnswering": insistAnswering,
        "useTyping": useTyping,
        "answerInGroups": answerInGroups,
        "answerInPrivate": answerInPrivate,
        "autoCloseTime": autoCloseTime === 0 ? '' : autoCloseTime,
        "queuesSequence": JSON.stringify(selectedQueueIds),
        "ignoreChatbot": values.ignoreChatbot,
      };

      // Adicionar mensagens extras apenas se tiverem valor
      if (values.greetingMessage2) dataToSend.greetingMessage2 = values.greetingMessage2;
      if (values.greetingMessage3) dataToSend.greetingMessage3 = values.greetingMessage3;
      if (values.message2) dataToSend.message2 = values.message2;
      if (values.message3) dataToSend.message3 = values.message3;
      if (values.farewellMessage2) dataToSend.farewellMessage2 = values.farewellMessage2;
      if (values.farewellMessage3) dataToSend.farewellMessage3 = values.farewellMessage3;
      if (values.closedMessage2) dataToSend.closedMessage2 = values.closedMessage2;
      if (values.closedMessage3) dataToSend.closedMessage3 = values.closedMessage3;

      // Para canal API Oficial (UniService): só persistimos o contrato.
      // Todas as credenciais Meta (token, phoneNumberId, businessId, etc)
      // são gerenciadas pelo microserviço — UniTicket não tem nem precisa
      // delas.
      if (channel === 'uniservice') {
        dataToSend.uniserviceContract = String(values.uniserviceContract || "").trim();
      }

      console.info(dataToSend);

      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, dataToSend);
      } else {
        await api.post("/whatsapp", dataToSend);
      }

      ToastSuccess(i18n.t("whatsappModal.success"));
      handleClose();
      resetForm();
    } catch (err) {
      setIsSubmitting(false);
      ToastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setActiveField(null);
    setFocusedField(null);
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
  };

  const settingAutoClose = (time) => {
    const hours = time.hour();
    const minutes = time.minute();
    const seconds = time.second();
    const toMilliseconds = (hours, minutes, seconds) =>
      (hours * 60 * 60 + minutes * 60 + seconds) * 1000;
    const convertToMilliseconds = toMilliseconds(hours, minutes, seconds);
    setTimeToAutoClose(time);
    setTimeToAutoCloseMilliseconds(convertToMilliseconds);
  }

  const changeAutoClose = (event) => {
    setAutoClose(event.target.checked);
  };

  const changeUseTyping = (event) => {
    setUseTyping(event.target.checked);
  };

  const changeInsistAnswering = (event) => {
    setInsistAnswering(event.target.checked);
  };

  const handleChangeChannel = (newChannel) => {
    // Only change channel if not editing existing connection
    if (!whatsAppId) {
      setChannel(newChannel);
    }
  };

  const getChannelIcon = (channelValue) => {
    const foundChannel = channels.find(c => c.value === channelValue);
    if (foundChannel) {
      const Icon = foundChannel.Icon;
      return <Icon className={classes.channelIcon} style={{ color: foundChannel.color }} />;
    }
    return null;
  };

  const handleInsertKeyword = (keyword, setFieldValue, values) => {
    if (!activeField || !setFieldValue) return;
    const currentValue = values[activeField] || '';
    const cursorPosition = fieldSelectionStart;
    const newValue = currentValue.substring(0, cursorPosition) +
      keyword +
      currentValue.substring(cursorPosition);
    setFieldValue(activeField, newValue);

    setTimeout(() => {
      const textFieldRef = getTextFieldRefByName(activeField);
      if (textFieldRef?.current) {
        const textArea = textFieldRef.current.querySelector('textarea');
        if (textArea) {
          textArea.focus();
          const newPosition = cursorPosition + keyword.length;
          textArea.setSelectionRange(newPosition, newPosition);
        }
      }
    }, 50);

    setInsertedKeyword(keyword);
    setShowSuccess(true);

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }

    successTimeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  };

  const getTextFieldRefByName = (name) => {
    switch (name) {
      case 'greetingMessage': return greetingMessageRef;
      case 'greetingMessage2': return greetingMessage2Ref;
      case 'greetingMessage3': return greetingMessage3Ref;
      case 'message': return messageRef;
      case 'message2': return message2Ref;
      case 'message3': return message3Ref;
      case 'farewellMessage': return farewellMessageRef;
      case 'farewellMessage2': return farewellMessage2Ref;
      case 'farewellMessage3': return farewellMessage3Ref;
      case 'closedMessage': return closedMessageRef;
      case 'closedMessage2': return closedMessage2Ref;
      case 'closedMessage3': return closedMessage3Ref;
      case 'autoCloseMessage': return autoCloseMessageRef;
      default: return null;
    }
  };

  const handleSelectionChange = (e) => {
    if (activeField) {
      setFieldSelectionStart(e.target.selectionStart);
      setFieldSelectionEnd(e.target.selectionEnd);
    }
  };

  const handleTextFieldKeyUp = (e) => {
    if (activeField) {
      setFieldSelectionStart(e.target.selectionStart);
      setFieldSelectionEnd(e.target.selectionEnd);
    }
  };

  const handleTextFieldMouseUp = (e) => {
    if (activeField) {
      setFieldSelectionStart(e.target.selectionStart);
      setFieldSelectionEnd(e.target.selectionEnd);
    }
  };

  const handleFieldFocus = (fieldName) => {
    setFocusedField(fieldName);
    setActiveField(fieldName);
    const textFieldRef = getTextFieldRefByName(fieldName);
    if (textFieldRef?.current) {
      const inputElement = textFieldRef.current.querySelector('textarea');
      if (inputElement) {
        setFieldSelectionStart(inputElement.selectionStart);
        setFieldSelectionEnd(inputElement.selectionEnd);
      }
    }
  };

  const handleFieldBlur = () => {
    setFocusedField(null);
  };

  const handleMessageTabChange = (event, newValue) => {
    setActiveMessageTab(newValue);
  };

  // Determinar se as mensagens devem ser mostradas
  const shouldShowMessages = !whatsApp.ignoreChatbot;

  if (loading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        className={classes.dialogRoot}
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" gap={1.2}>
            <SettingsIcon fontSize="small" />
            <Typography variant="h6">
              {whatsAppId
                ? i18n.t("whatsappModal.title.edit")
                : i18n.t("whatsappModal.title.add")}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            className={classes.closeButton}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <Box className={classes.loadingContainer}>
            <CircularProgress size={42} className={classes.loadingCircle} />
            <Typography variant="body1">
              Carregando configurações...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const getChannelData = (channelValue) => {
    return channels.find(c => c.value === channelValue) || {};
  };

  const channelData = getChannelData(channel);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className={classes.dialogRoot}
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.titleWithIcon}>
          {getChannelIcon(channel)}
          <Typography variant="h6">
            {whatsAppId
              ? `${i18n.t("whatsappModal.title.edit")}: ${whatsApp.name}`
              : i18n.t("whatsappModal.title.add")}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          className={classes.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Formik
        initialValues={whatsApp}
        enableReinitialize={true}
        validationSchema={SessionSchema}
        onSubmit={handleSaveWhatsApp}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue, handleSubmit }) => {
          console.log('Formik render - errors:', errors);
          console.log('Formik render - isSubmitting:', isSubmitting);
          return (
            <Form onSubmit={(e) => {
              console.log('Form onSubmit triggered');
              handleSubmit(e);
            }}>
              <Field
                as={TextField}
                type="hidden"
                name="companyId"
                style={{ display: 'none' }}
              />

              <DialogContent className={classes.dialogContent}>
                {/* CONFIGURAÇÕES BÁSICAS */}
                <Paper elevation={0} className={classes.formSection}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <SettingsIcon className={classes.icon} />
                    Configurações Básicas
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                      <Typography className={classes.fieldLabel}>
                        <BusinessIcon fontSize="small" /> Nome da Conexão
                      </Typography>
                      <Field
                        as={TextField}
                        placeholder="Digite um nome para identificar esta conexão"
                        autoFocus
                        name="name"
                        fullWidth
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        size="small"
                        className={classes.textField}
                      />
                    </Grid>

                    {/* SELEÇÃO DE CANAL */}
                    <Grid item xs={12}>
                      <Typography className={classes.fieldLabel}>
                        <ForumIcon fontSize="small" /> Canal de Comunicação
                        <Tooltip
                          title="Selecione o canal que será usado para comunicação com os clientes"
                          arrow
                          classes={{ tooltip: classes.infoTooltip }}
                        >
                          <HelpOutlineIcon className={classes.helpIcon} />
                        </Tooltip>
                      </Typography>

                      {/* Agrupado por família (WhatsApp / Outros) — deixa
                          explícito que as 3 primeiras opções são variantes do
                          mesmo WhatsApp, que era a origem da confusão. */}
                      {channelGroups.map((groupName) => (
                        <Box key={groupName} sx={{ mb: 1 }}>
                          <Typography className={classes.channelGroupLabel}>
                            {groupName}
                          </Typography>
                          <Grid container spacing={1.5}>
                            {channels
                              .filter((c) => c.group === groupName)
                              .map(({ value, disabled, color, Icon, labelKey, description, badge }) => (
                                <Grid item xs={12} sm={6} md={4} key={value}>
                                  <Box
                                    className={`${classes.channelCard} ${channel === value ? 'selected' : ''}`}
                                    onClick={() => (disabled ? {} : handleChangeChannel(value))}
                                    sx={{
                                      opacity: disabled || !!whatsAppId ? 0.65 : 1,
                                      cursor: disabled || !!whatsAppId ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    {channel === value && (
                                      <CheckCircleIcon className={classes.channelCheckmark} />
                                    )}

                                    {badge && (
                                      <Box className={classes.channelBadge}>{badge}</Box>
                                    )}

                                    <Box
                                      className={classes.channelIconWrapper}
                                      sx={{ backgroundColor: alpha(color, 0.15), color }}
                                    >
                                      <Icon />
                                    </Box>

                                    <Typography className={classes.channelTitle}>
                                      {i18n.t(labelKey)}
                                      {disabled && " (Em breve)"}
                                    </Typography>
                                    <Typography className={classes.channelDescription}>
                                      {description}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                          </Grid>
                        </Box>
                      ))}

                      {whatsAppId && (
                        <Typography className={classes.hintText}>
                          <InfoOutlinedIcon fontSize="small" />
                          O canal não pode ser alterado após a criação
                        </Typography>
                      )}

                      {/* Avisos específicos do canal */}
                      {channel === 'whatsapp' && (
                        <Box className={classes.warningAlert}>
                          <WarningAmberIcon />
                          <Typography>
                            <strong>Aviso:</strong> A versão gratuita pode sofrer perda ocasional de mensagens e respostas.
                            Existe um pequeno risco de banimento do número pela Meta. Recomendamos o WhatsApp (Avançado) ou a API Oficial para uso corporativo.
                          </Typography>
                        </Box>
                      )}

                      {channel === 'whatsgo' && (
                        <Box className={classes.successAlert}>
                          <WhatsAppIcon />
                          <Typography>
                            <strong>WhatsApp (Avançado):</strong> conexão via QR Code mais estável,
                            ideal para o dia a dia. Após salvar, escaneie o QR Code na tabela de conexões.
                          </Typography>
                        </Box>
                      )}

                    </Grid>
                  </Grid>

                  {/*
                    Bloco específico para conexão WhatsApp via API Oficial.
                    Apenas o campo "Contrato" é necessário aqui — o resto
                    (credenciais Meta, Embedded Signup, Coexistência) é
                    gerenciado pelo microserviço UniService através do
                    iframe que abre quando o usuário clica em "Login com
                    Facebook" na tabela de conexões.
                  */}
                  {channel === 'uniservice' && (
                    <>
                      <Divider className={classes.divider} />

                      <Box className={classes.successAlert}>
                        <FacebookIcon />
                        <Typography>
                          <strong>API Oficial via UniService:</strong> preencha o contrato
                          da empresa abaixo. Após salvar, clique em <em>"Login com Facebook"</em> na
                          tabela de conexões para finalizar a vinculação via Embedded Signup.
                        </Typography>
                      </Box>

                      <Typography variant="subtitle2" className={classes.fieldLabel} sx={{ mt: 2 }}>
                        <FingerprintIcon fontSize="small" /> Contrato
                      </Typography>
                      <Field
                        as={TextField}
                        name="uniserviceContract"
                        placeholder="Ex: 2166"
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={touched.uniserviceContract && Boolean(errors.uniserviceContract)}
                        helperText={
                          (touched.uniserviceContract && errors.uniserviceContract) ||
                          "Identificador da empresa no Gestor. Usado pelo UniService para fazer o auto-login do iframe."
                        }
                        disabled={Boolean(whatsAppId && initialState?.uniserviceContract && whatsAppId)}
                        className={classes.textField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FingerprintIcon color="primary" fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </>
                  )}

                  {channel === 'telegram' && (
                    <>
                      <Divider className={classes.divider} />

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography className={classes.fieldLabel}>
                            <KeyIcon fontSize="small" /> Token da API do Telegram
                          </Typography>
                          <Box className={classes.tokenField}>
                            <Field
                              as={TextField}
                              placeholder="Cole aqui o token de API do seu bot Telegram"
                              type={showTokenPassword ? "text" : "password"}
                              fullWidth
                              name="token"
                              variant="outlined"
                              size="small"
                              error={touched.token && Boolean(errors.token)}
                              helperText={touched.token && errors.token}
                              className={classes.textField}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AttachFileIcon color="primary" fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />

                            <IconButton
                              className={classes.tokenVisibilityButton}
                              onClick={() => setShowTokenPassword(!showTokenPassword)}
                              aria-label={showTokenPassword ? "Ocultar token" : "Mostrar token"}
                              size="small"
                            >
                              {showTokenPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </Box>

                          <Typography variant="body2" className={classes.hintText}>
                            <TipsAndUpdatesIcon fontSize="small" />
                            Obtenha o token conversando com o @BotFather no Telegram
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {/* Novo checkbox para ignorar mensagens */}
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={whatsApp.ignoreChatbot}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setWhatsApp((prevState) => ({ ...prevState, ignoreChatbot: newValue }));
                            setFieldValue("ignoreChatbot", newValue);

                            // Limpar valores dos campos de mensagem quando ignoreChatbot for true
                            if (newValue) {
                              setFieldValue("greetingMessage", "");
                              setFieldValue("message", "");
                              setFieldValue("farewellMessage", "");
                              setFieldValue("closedMessage", "");
                              setWhatsApp((prevState) => ({
                                ...prevState,
                                greetingMessage: "",
                                message: "",
                                farewellMessage: "",
                                closedMessage: ""
                              }));
                            }
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={0.6}>
                          <DoNotDisturbIcon sx={{ fontSize: '1.1rem', color: theme => theme.palette.warning.main }} />
                          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {i18n.t("whatsappModal.ignoreChatbot.use") || "Não usar mensagens nesta conexão"}
                          </Typography>
                          <Tooltip
                            title={i18n.t("whatsappModal.ignoreChatbot.tooltip") || "Ao marcar esta opção, nenhuma mensagem automática será enviada nesta conexão"}
                            arrow
                            classes={{ tooltip: classes.infoTooltip }}
                          >
                            <HelpOutlineIcon className={classes.helpIcon} />
                          </Tooltip>
                        </Box>
                      }
                    />

                    {whatsApp.ignoreChatbot && (
                      <Typography className={classes.hintText} sx={{ mt: 1 }}>
                        <TipsAndUpdatesIcon fontSize="small" />
                        {i18n.t("whatsappModal.ignoreChatbot.hint") || "Com esta opção ativada, nenhuma mensagem de saudação, setores, despedida ou horário de funcionamento será enviada automaticamente."}
                      </Typography>
                    )}
                  </Box>

                  {/* OPÇÕES DE CONFIGURAÇÃO */}
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={whatsApp.ignoreChatbot ? 6 : 4}>
                      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.6), border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={autoClose}
                              onChange={changeAutoClose}
                              color="primary"
                              className={classes.switchRoot}
                              size="small"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={0.6}>
                              <Typography sx={{
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                color: autoClose ? theme.palette.text.primary : theme.palette.text.secondary,
                              }}>
                                {i18n.t("whatsappModal.autoClose")}
                              </Typography>
                              <Tooltip
                                title="Encerra automaticamente o atendimento após o tempo configurado"
                                arrow
                                classes={{ tooltip: classes.infoTooltip }}
                              >
                                <HelpOutlineIcon className={classes.helpIcon} />
                              </Tooltip>
                            </Box>
                          }
                          className={classes.compactFormControl}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={whatsApp.ignoreChatbot ? 6 : 4}>
                      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.6), border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={useTyping}
                              onChange={changeUseTyping}
                              color="primary"
                              className={classes.switchRoot}
                              size="small"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={0.6}>
                              <Typography sx={{
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                color: useTyping ? theme.palette.text.primary : theme.palette.text.secondary,
                              }}>
                                {i18n.t("whatsappModal.useTyping")}
                              </Typography>
                              <Tooltip
                                title="Mostra o indicador de 'digitando...'"
                                arrow
                                classes={{ tooltip: classes.infoTooltip }}
                              >
                                <HelpOutlineIcon className={classes.helpIcon} />
                              </Tooltip>
                            </Box>
                          }
                          className={classes.compactFormControl}
                        />
                      </Paper>
                    </Grid>
                    {!whatsApp.ignoreChatbot && (
                      <Grid item xs={12} sm={4}>
                        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.6), border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={insistAnswering}
                                onChange={changeInsistAnswering}
                                color="primary"
                                className={classes.switchRoot}
                                size="small"
                              />
                            }
                            label={
                              <Box display="flex" alignItems="center" gap={0.6}>
                                <Typography sx={{
                                  fontWeight: 500,
                                  fontSize: '0.9rem',
                                  color: insistAnswering ? theme.palette.text.primary : theme.palette.text.secondary,
                                }}>
                                  {i18n.t("whatsappModal.insistAnswering")}
                                </Typography>
                                <Tooltip
                                  title="Insiste por resposta do cliente quando necessário"
                                  arrow
                                  classes={{ tooltip: classes.infoTooltip }}
                                >
                                  <HelpOutlineIcon className={classes.helpIcon} />
                                </Tooltip>
                              </Box>
                            }
                            className={classes.compactFormControl}
                          />
                        </Paper>
                      </Grid>
                    )}

                    {/* Responder grupos / privado — só em baileys/whatsgo */}
                    {(channel === 'whatsapp' || channel === 'whatsgo') && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.6), border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={answerInGroups}
                                  onChange={(e) => setAnswerInGroups(e.target.checked)}
                                  color="primary"
                                  className={classes.switchRoot}
                                  size="small"
                                />
                              }
                              label={
                                <Box display="flex" alignItems="center" gap={0.6}>
                                  <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: answerInGroups ? theme.palette.text.primary : theme.palette.text.secondary }}>
                                    Responder grupos
                                  </Typography>
                                  <Tooltip title="Atende e responde mensagens vindas de grupos" arrow classes={{ tooltip: classes.infoTooltip }}>
                                    <HelpOutlineIcon className={classes.helpIcon} />
                                  </Tooltip>
                                </Box>
                              }
                              className={classes.compactFormControl}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.background.default, 0.6), border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={answerInPrivate}
                                  onChange={(e) => setAnswerInPrivate(e.target.checked)}
                                  color="primary"
                                  className={classes.switchRoot}
                                  size="small"
                                />
                              }
                              label={
                                <Box display="flex" alignItems="center" gap={0.6}>
                                  <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: answerInPrivate ? theme.palette.text.primary : theme.palette.text.secondary }}>
                                    Responder privado
                                  </Typography>
                                  <Tooltip title="Atende e responde mensagens privadas (1:1)" arrow classes={{ tooltip: classes.infoTooltip }}>
                                    <HelpOutlineIcon className={classes.helpIcon} />
                                  </Tooltip>
                                </Box>
                              }
                              className={classes.compactFormControl}
                            />
                          </Paper>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>

                {/* SEÇÃO DE AUTO FECHAMENTO */}
                {autoClose && (
                  <Paper elevation={0} className={classes.formSection}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      <AccessTimeIcon className={classes.icon} />
                      Auto Fechamento
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography className={classes.fieldLabel}>
                          <ScheduleIcon fontSize="small" /> Tempo para Auto Fechamento
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                          <TimePicker
                            value={timeToAutoClose}
                            onChange={(newValue) => {
                              settingAutoClose(newValue);
                            }}
                            ampm={false}
                            className={classes.timePicker}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                variant: 'outlined',
                                placeholder: "Selecione o tempo",
                                className: classes.textField
                              }
                            }}
                          />
                        </LocalizationProvider>

                        <Box className={classes.timeInfo}>
                          <AccessTimeIcon />
                          <Typography>
                            {msToTime(autoCloseTime)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextFieldWithKeywords
                          fieldName="autoCloseMessage"
                          label="Mensagem de Auto Fechamento"
                          placeholder="Mensagem enviada ao fechar automaticamente"
                          fieldRef={autoCloseMessageRef}
                          values={values}
                          touched={touched}
                          errors={errors}
                          setFieldValue={setFieldValue}
                          handleFieldFocus={handleFieldFocus}
                          handleFieldBlur={handleFieldBlur}
                          handleSelectionChange={handleSelectionChange}
                          handleTextFieldKeyUp={handleTextFieldKeyUp}
                          handleTextFieldMouseUp={handleTextFieldMouseUp}
                          handleInsertKeyword={handleInsertKeyword}
                          focusedField={focusedField}
                          classes={classes}
                          icon={<MessageIcon fontSize="small" />}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* SEÇÃO DE MENSAGENS - Só mostra se ignoreChatbot for false */}
                {shouldShowMessages && (
                  <Paper elevation={0} className={classes.formSection}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      <MessageIcon className={classes.icon} />
                      Mensagens
                    </Typography>

                    <Box className={classes.messageTabsContainer}>
                      <Tabs
                        value={activeMessageTab}
                        onChange={handleMessageTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        className={classes.messageTabs}
                      >
                        <Tab
                          label="Saudação"
                          icon={<EmojiEmotionsOutlinedIcon className={classes.messageTabIcon} />}
                          iconPosition="start"
                        />

                        <Tab
                          label="Setores"
                          icon={<GroupsIcon className={classes.messageTabIcon} />}
                          iconPosition="start"
                          disabled={selectedQueueIds.length === 0}
                        />

                        <Tab
                          label="Despedida"
                          icon={<FormatQuoteIcon className={classes.messageTabIcon} />}
                          iconPosition="start"
                        />

                        <Tab
                          label="Expediente Fechado"
                          icon={<ScheduleIcon className={classes.messageTabIcon} />}
                          iconPosition="start"
                        />
                      </Tabs>
                    </Box>

                    <Grid container>
                      <Grid item xs={12}>
                        {activeMessageTab === 0 && (
                          <Box>
                            {/* Sub-tabs para mensagens de saudação */}
                            <Box sx={{
                              borderBottom: 1,
                              borderColor: 'divider',
                              mb: 2,
                              backgroundColor: theme => alpha(theme.palette.background.default, 0.3),
                              borderRadius: '8px 8px 0 0',
                              p: 1
                            }}>
                              <Tabs
                                value={greetingTab}
                                onChange={(e, newValue) => setGreetingTab(newValue)}
                                variant="fullWidth"
                                sx={{
                                  minHeight: 36,
                                  '& .MuiTab-root': {
                                    minHeight: 36,
                                    fontSize: '0.8rem',
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                  }
                                }}
                              >
                                <Tab label="Saudação 1" />
                                <Tab label="Saudação 2" />
                                <Tab label="Saudação 3" />
                              </Tabs>
                            </Box>

                            {greetingTab === 0 && (
                              <TextFieldWithKeywords
                                fieldName="greetingMessage"
                                label="Mensagem de Saudação 1"
                                placeholder="Digite a primeira mensagem de saudação"
                                fieldRef={greetingMessageRef}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<EmojiEmotionsOutlinedIcon fontSize="small" />}
                              />
                            )}

                            {greetingTab === 1 && (
                              <TextFieldWithKeywords
                                fieldName="greetingMessage2"
                                label="Mensagem de Saudação 2"
                                placeholder="Digite a segunda mensagem de saudação (opcional)"
                                fieldRef={greetingMessage2Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<EmojiEmotionsOutlinedIcon fontSize="small" />}
                              />
                            )}

                            {greetingTab === 2 && (
                              <TextFieldWithKeywords
                                fieldName="greetingMessage3"
                                label="Mensagem de Saudação 3"
                                placeholder="Digite a terceira mensagem de saudação (opcional)"
                                fieldRef={greetingMessage3Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<EmojiEmotionsOutlinedIcon fontSize="small" />}
                              />
                            )}
                          </Box>
                        )}

                        {activeMessageTab === 1 && selectedQueueIds.length > 0 && (
                          <Box>
                            {/* Sub-tabs para mensagens de setores */}
                            <Box sx={{
                              borderBottom: 1,
                              borderColor: 'divider',
                              mb: 2,
                              backgroundColor: theme => alpha(theme.palette.background.default, 0.3),
                              borderRadius: '8px 8px 0 0',
                              p: 1
                            }}>
                              <Tabs
                                value={messageTab}
                                onChange={(e, newValue) => setMessageTab(newValue)}
                                variant="fullWidth"
                                sx={{
                                  minHeight: 36,
                                  '& .MuiTab-root': {
                                    minHeight: 36,
                                    fontSize: '0.8rem',
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                  }
                                }}
                              >
                                <Tab label="Setores 1" />
                                <Tab label="Setores 2" />
                                <Tab label="Setores 3" />
                              </Tabs>
                            </Box>

                            {messageTab === 0 && (
                              <TextFieldWithKeywords
                                fieldName="message"
                                label="Mensagem de Setores 1"
                                placeholder="Digite a primeira mensagem para seleção de setores"
                                fieldRef={messageRef}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<GroupsIcon fontSize="small" />}
                              />
                            )}

                            {messageTab === 1 && (
                              <TextFieldWithKeywords
                                fieldName="message2"
                                label="Mensagem de Setores 2"
                                placeholder="Digite a segunda mensagem para seleção de setores (opcional)"
                                fieldRef={message2Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<GroupsIcon fontSize="small" />}
                              />
                            )}

                            {messageTab === 2 && (
                              <TextFieldWithKeywords
                                fieldName="message3"
                                label="Mensagem de Setores 3"
                                placeholder="Digite a terceira mensagem para seleção de setores (opcional)"
                                fieldRef={message3Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<GroupsIcon fontSize="small" />}
                              />
                            )}
                          </Box>
                        )}

                        {activeMessageTab === 2 && (
                          <Box>
                            {/* Sub-tabs para mensagens de despedida */}
                            <Box sx={{
                              borderBottom: 1,
                              borderColor: 'divider',
                              mb: 2,
                              backgroundColor: theme => alpha(theme.palette.background.default, 0.3),
                              borderRadius: '8px 8px 0 0',
                              p: 1
                            }}>
                              <Tabs
                                value={farewellTab}
                                onChange={(e, newValue) => setFarewellTab(newValue)}
                                variant="fullWidth"
                                sx={{
                                  minHeight: 36,
                                  '& .MuiTab-root': {
                                    minHeight: 36,
                                    fontSize: '0.8rem',
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                  }
                                }}
                              >
                                <Tab label="Despedida 1" />
                                <Tab label="Despedida 2" />
                                <Tab label="Despedida 3" />
                              </Tabs>
                            </Box>

                            {farewellTab === 0 && (
                              <TextFieldWithKeywords
                                fieldName="farewellMessage"
                                label="Mensagem de Despedida 1"
                                placeholder="Digite a primeira mensagem de despedida"
                                fieldRef={farewellMessageRef}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<FormatQuoteIcon fontSize="small" />}
                              />
                            )}

                            {farewellTab === 1 && (
                              <TextFieldWithKeywords
                                fieldName="farewellMessage2"
                                label="Mensagem de Despedida 2"
                                placeholder="Digite a segunda mensagem de despedida (opcional)"
                                fieldRef={farewellMessage2Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<FormatQuoteIcon fontSize="small" />}
                              />
                            )}

                            {farewellTab === 2 && (
                              <TextFieldWithKeywords
                                fieldName="farewellMessage3"
                                label="Mensagem de Despedida 3"
                                placeholder="Digite a terceira mensagem de despedida (opcional)"
                                fieldRef={farewellMessage3Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<FormatQuoteIcon fontSize="small" />}
                              />
                            )}
                          </Box>
                        )}

                        {activeMessageTab === 3 && (
                          <Box>
                            {/* Sub-tabs para mensagens de expediente fechado */}
                            <Box sx={{
                              borderBottom: 1,
                              borderColor: 'divider',
                              mb: 2,
                              backgroundColor: theme => alpha(theme.palette.background.default, 0.3),
                              borderRadius: '8px 8px 0 0',
                              p: 1
                            }}>
                              <Tabs
                                value={closedMessageTab}
                                onChange={(e, newValue) => setClosedMessageTab(newValue)}
                                variant="fullWidth"
                                sx={{
                                  minHeight: 36,
                                  '& .MuiTab-root': {
                                    minHeight: 36,
                                    fontSize: '0.8rem',
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                  }
                                }}
                              >
                                <Tab label="Expediente 1" />
                                <Tab label="Expediente 2" />
                                <Tab label="Expediente 3" />
                              </Tabs>
                            </Box>

                            {closedMessageTab === 0 && (
                              <TextFieldWithKeywords
                                fieldName="closedMessage"
                                label="Mensagem de Expediente Fechado 1"
                                placeholder="Primeira mensagem para quando o expediente estiver fechado"
                                fieldRef={closedMessageRef}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<ScheduleIcon fontSize="small" />}
                              />
                            )}

                            {closedMessageTab === 1 && (
                              <TextFieldWithKeywords
                                fieldName="closedMessage2"
                                label="Mensagem de Expediente Fechado 2"
                                placeholder="Segunda mensagem para quando o expediente estiver fechado (opcional)"
                                fieldRef={closedMessage2Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<ScheduleIcon fontSize="small" />}
                              />
                            )}

                            {closedMessageTab === 2 && (
                              <TextFieldWithKeywords
                                fieldName="closedMessage3"
                                label="Mensagem de Expediente Fechado 3"
                                placeholder="Terceira mensagem para quando o expediente estiver fechado (opcional)"
                                fieldRef={closedMessage3Ref}
                                values={values}
                                touched={touched}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                handleFieldFocus={handleFieldFocus}
                                handleFieldBlur={handleFieldBlur}
                                handleSelectionChange={handleSelectionChange}
                                handleTextFieldKeyUp={handleTextFieldKeyUp}
                                handleTextFieldMouseUp={handleTextFieldMouseUp}
                                handleInsertKeyword={handleInsertKeyword}
                                focusedField={focusedField}
                                classes={classes}
                                icon={<ScheduleIcon fontSize="small" />}
                              />
                            )}
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* SEÇÃO DE SETORES */}
                <Paper elevation={0} className={classes.formSection}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <GroupsIcon className={classes.icon} />
                    Setores de Atendimento
                  </Typography>

                  <Grid container>
                    <Grid item xs={12}>
                      <QueueSelect
                        selectedQueueIds={selectedQueueIds}
                        onChange={selectedIds => setSelectedQueueIds(selectedIds)}
                      />

                      <Typography variant="body2" className={classes.hintText} sx={{ mt: 2 }}>
                        <LightbulbOutlinedIcon fontSize="small" />
                        {whatsApp.ignoreChatbot
                          ? "Os setores selecionados estarão apenas vinculados à conexão, sem serem apresentados como opções ao cliente."
                          : "Os setores selecionados serão apresentados ao cliente na ordem que você selecionar."
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </DialogContent>

              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  color="inherit"
                  disabled={isSubmitting}
                  variant="outlined"
                  size="medium"
                  className={classes.cancelButton}
                  startIcon={<CloseIcon fontSize="small" />}
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  size="medium"
                  className={classes.saveButton}
                  startIcon={isSubmitting ? <CircularProgress size={18} className={classes.saveProgress} /> : <SaveIcon fontSize="small" />}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                </Button>
              </DialogActions>

              {showSuccess && (
                <Box className={classes.floatingSuccessAlert}>
                  <CheckCircleIcon />
                  <Typography>
                    Palavra-chave inserida
                  </Typography>
                </Box>
              )}
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CreateOrEditWhatsAppModal;